import { TAxiosError } from '@mgm/common/types';
import { useRequest } from '@mgm/common/lib';
import { CONTROLLERS } from '../utils/enums';
import {
  TAddCommissionPlanReq,
  TAddCommissionTeamReq,
  TAssignCommissionPlansReq,
  TUpdateCommissionTeamMembersReq,
  TUpdateCommissionTeamPlanReq,
} from '../types/requests';
import { TCommissionPlan } from '../types';
import { BASE_URL } from '../../config';
import { getIdToken } from '../utils/helpers';

export function CommissionsController() {
  const {
    axiosRequest,
    initRequestOptions,
  } = useRequest(BASE_URL);

  const controllerName = CONTROLLERS.COMMISSIONS;

  const getCommissionsPlans = <T>(data: { shopName: string }): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/plans/data`, options);
  };

  const addCommissionPlan = <T>(data: TAddCommissionPlanReq): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/plans/add`, options);
  };

  const editCommissionPlan = <T>(data: TAddCommissionPlanReq): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/plans/update`, options);
  };

  const deleteCommissionPlan = <T>(data: { shopName: string, planId: string }): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/plans/delete`, options);
  };

  const assignCommissionPlan = <T>(data: TAssignCommissionPlansReq): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/plans/assign`, options);
  };

  const assignPlanBulk = <T>(data: {
    shopName: string,
    assignData: Array<{ commissionPlan: TCommissionPlan, memberEmails: string[] }>
  }): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/plans/assign_bulk`, options);
  };

  const getTeamsData = <T>(data: { shopName: string }): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/teams/data`, options);
  };

  const addCommissionTeam = <T>(data: TAddCommissionTeamReq): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/teams/add`, options);
  };

  const updateCommissionTeamMembers = <T>(data: TUpdateCommissionTeamMembersReq): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/teams/update_members`, options);
  };

  const updateCommissionTeamPlan = <T>(data: TUpdateCommissionTeamPlanReq): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/teams/update_plan`, options);
  };

  const deleteCommissionTeam = <T>(data: { shopName: string, teamId: number }): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/teams/delete`, options);
  };

  const getProductVariant = <T>(data: {
    shopName: string,
    variantId?: string,
    sku?: string,
    uidType: 'variantId' | 'sku'
  }): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/products/get_variant`, options);
  };

  return {
    getCommissionsPlans,
    addCommissionPlan,
    deleteCommissionPlan,
    editCommissionPlan,
    assignCommissionPlan,
    assignPlanBulk,
    getTeamsData,
    addCommissionTeam,
    updateCommissionTeamMembers,
    updateCommissionTeamPlan,
    deleteCommissionTeam,
    getProductVariant,
  };
}
